import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import classes from '../departments.module.scss';
import { createCn } from '../../../helpers/utils';
import { DepartmentItem } from '../../../services/api.service';

const cn = createCn('departments', classes);

const PRIORITY_MIN = 0;
const PRIORITY_MAX = 100;
const STEP = 5;
const DEFAULT_NEW_PRIORITY = 50;

function clampPriority(value: number): number {
  if (Number.isNaN(value)) return PRIORITY_MIN;
  return Math.min(PRIORITY_MAX, Math.max(PRIORITY_MIN, Math.round(value)));
}

interface DepartmentRowProps {
  department: DepartmentItem;
  saving: boolean;
  deleting: boolean;
  onSavePriority: (priority: number) => void;
  onDelete: () => void;
}

const DepartmentRow = ({ department, saving, deleting, onSavePriority, onDelete }: DepartmentRowProps) => {
  const [draft, setDraft] = useState(String(department.priority));

  useEffect(() => {
    setDraft(String(department.priority));
  }, [department.priority]);

  const commit = (value: number) => {
    const clamped = clampPriority(value);
    setDraft(String(clamped));
    if (clamped !== department.priority) onSavePriority(clamped);
  };

  return (
    <div className={cn('dept-row')}>
      <div className={cn('dept-name')} title={department.name}>{department.name}</div>

      <div className={cn('stepper')}>
        <button
          type="button"
          className={cn('stepper-btn')}
          onClick={() => commit(department.priority - STEP)}
          disabled={saving}
          aria-label={`Уменьшить приоритет отдела «${department.name}»`}
        >
          −
        </button>
        <input
          type="number"
          className={cn('stepper-input')}
          min={PRIORITY_MIN}
          max={PRIORITY_MAX}
          value={draft}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commit(Number(draft))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          aria-label={`Приоритет отдела «${department.name}»`}
        />
        <button
          type="button"
          className={cn('stepper-btn')}
          onClick={() => commit(department.priority + STEP)}
          disabled={saving}
          aria-label={`Увеличить приоритет отдела «${department.name}»`}
        >
          +
        </button>
      </div>

      <div className={cn('dept-count')}>{department.user_count} сотр.</div>

      <button
        type="button"
        className={cn('icon-btn', { danger: true })}
        onClick={onDelete}
        disabled={deleting}
        title="Удалить отдел"
        aria-label={`Удалить отдел «${department.name}»`}
      >
        <Trash2 size={16} /> {deleting ? 'Удаление…' : 'Удалить'}
      </button>
    </div>
  );
};

interface DepartmentCreateFormProps {
  creating: boolean;
  onCreate: (name: string, priority: number) => void;
}

const DepartmentCreateForm = ({ creating, onCreate }: DepartmentCreateFormProps) => {
  const [name, setName] = useState('');
  const [priority, setPriority] = useState(String(DEFAULT_NEW_PRIORITY));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreate(trimmed, clampPriority(Number(priority) || DEFAULT_NEW_PRIORITY));
    setName('');
    setPriority(String(DEFAULT_NEW_PRIORITY));
  };

  return (
    <form className={cn('create-form')} onSubmit={submit}>
      <input
        type="text"
        className={cn('create-input')}
        placeholder="Название нового отдела"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={creating}
        aria-label="Название нового отдела"
      />
      <input
        type="number"
        className={cn('create-priority-input')}
        min={PRIORITY_MIN}
        max={PRIORITY_MAX}
        value={priority}
        onChange={(e) => setPriority(e.target.value)}
        disabled={creating}
        aria-label="Приоритет нового отдела"
      />
      <button type="submit" className={cn('create-btn')} disabled={creating || !name.trim()}>
        <Plus size={16} /> {creating ? 'Создание…' : 'Создать отдел'}
      </button>
    </form>
  );
};

interface DepartmentListProps {
  departments: DepartmentItem[];
  creating: boolean;
  savingId: number | null;
  deletingId: number | null;
  onCreate: (name: string, priority: number) => void;
  onUpdatePriority: (id: number, priority: number) => void;
  onDelete: (department: DepartmentItem) => void;
}

const DepartmentList = ({
  departments,
  creating,
  savingId,
  deletingId,
  onCreate,
  onUpdatePriority,
  onDelete,
}: DepartmentListProps) => {
  return (
    <div className={cn('section')}>
      <div className={cn('section-title')}>Отделы ({departments.length})</div>

      {departments.length === 0 ? (
        <div className={cn('empty')}>Отделов пока нет</div>
      ) : (
        <div className={cn('dept-list')}>
          {departments.map((department) => (
            <DepartmentRow
              key={department.id}
              department={department}
              saving={savingId === department.id}
              deleting={deletingId === department.id}
              onSavePriority={(priority) => onUpdatePriority(department.id, priority)}
              onDelete={() => onDelete(department)}
            />
          ))}
        </div>
      )}

      <DepartmentCreateForm creating={creating} onCreate={onCreate} />
    </div>
  );
};

export default DepartmentList;
